import { useId, useMemo, useRef, useState } from 'react';

import { useUIContext } from 'lib/context/UIContext/UIContext';
import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';

import { CaptionDesktop, CaptionMobile } from './Caption';

const Disclosure = ({
  showMore = false,
  title,
  caption,
  icon,
  children,
  defaultOpen = false,
  isOpen: controlledOpen,
  onToggle,
  className,
  disableOpen = false,
  info,
  buttonClassName,
  bodyClassName,
  disabled = false,
  iconColor,
  iconSize = 18,
  customTitle
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [showInfo, setShowInfo] = useState(false);

  const { device } = useUIContext();
  const isMobile = device === 'mobile' || device === 'tablet-sm';

  const isControlled = typeof controlledOpen === 'boolean';
  const isOpen = useMemo(
    () => (isControlled ? controlledOpen : internalOpen),
    [controlledOpen, isControlled, internalOpen]
  );

  const contentRef = useRef(null);
  const contentId = useId();

  const handleToggle = () => {
    onToggle?.(!isOpen);
    if (!isControlled) setInternalOpen((prev) => !prev);
  };

  const handleShowInfo = () => {
    setShowInfo((prev) => !prev);
  };

  const shouldSetHeight = isOpen && !disableOpen;

  return (
    <div
      className={cn(
        'h-fit w-full rounded-lg border border-solid transition-all duration-200 hover:border-primary-300 hover:shadow-card-selected hover:ring-2 hover:ring-primary-200',
        isOpen
          ? 'border-primary-300 bg-white shadow-card-selected ring-2 ring-primary-200'
          : 'overflow-hidden border-neutral-200 bg-white',
        className
      )}>
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'flex w-full cursor-pointer gap-2 rounded-lg bg-white px-3 py-3.5 text-left',
          buttonClassName,
          disabled && 'opacity-50'
        )}
        aria-expanded={isOpen && !disableOpen}
        aria-controls={contentId}
        disabled={disabled}>
        {icon && typeof icon === 'string' ? (
          <Icon
            icon={icon}
            size={iconSize}
            color={iconColor ?? (isOpen ? 'primary' : 'neutral')}
            shade={isOpen ? 600 : 700}
            className="self-start"
          />
        ) : (
          icon || null
        )}
        <div className="flex w-full flex-col gap-2">
          <div className={cn('flex w-full justify-between gap-2', bodyClassName)}>
            {customTitle ? (
              customTitle
            ) : (
              <Typography
                variant="p"
                size="small"
                color="neutral-800"
                weight="semibold"
                className="cursor-pointer leading-5">
                {title}
              </Typography>
            )}

            {(caption || info) && (
              <>
                {isMobile && showMore ? (
                  <Typography
                    variant="p"
                    size="xs"
                    color="primary-500"
                    weight="medium"
                    className="cursor-pointer leading-4"
                    onClick={handleShowInfo}>
                    {showInfo ? 'Show less' : 'Show more'}
                  </Typography>
                ) : (
                  <CaptionDesktop caption={caption} info={info} />
                )}
              </>
            )}
          </div>
          {isMobile && info && showInfo && <CaptionMobile info={info} />}
        </div>
      </button>
      <div
        ref={contentRef}
        style={{
          maxHeight: shouldSetHeight ? contentRef.current?.scrollHeight : 0,
          minHeight: shouldSetHeight ? 'fit-content' : 0
        }}
        id={contentId}
        role="region"
        aria-label={typeof title === 'string' ? title : 'Section'}
        className="overflow-hidden px-3 transition-all duration-300">
        <div className="py-2">{children}</div>
      </div>
    </div>
  );
};

export default Disclosure;
